import React from "react";
import { useLang } from "../LangContext";
import { ArrowRight, Sparkles, Spade, Dices, Heart, Zap } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/0bf0a0ef-25eb-404f-ad9c-37c974297201/images/6b97155279dbad1222a5930bbc0cb73472b0e78025d504ac9c9b3c96c09207f8.png";

export const Hero = () => {
  const { t } = useLang();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden baj-noise"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a]/70 via-[#0a0e1a]/55 to-[#0a0e1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_80%_15%,rgba(255,107,0,0.28),transparent_60%),radial-gradient(700px_500px_at_10%_85%,rgba(139,92,246,0.32),transparent_60%)]" />
      </div>

      {/* Floating decorative elements */}
      <FloatingDeco />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="max-w-4xl">
          <div
            className="inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-1.5 glass-strong text-xs sm:text-sm font-semibold"
            data-testid="hero-badge"
          >
            <span className="w-7 h-7 rounded-full bg-baj-grad flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="text-slate-100">{t.hero.badge}</span>
          </div>

          <h1
            className="font-display mt-7 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[0.95] tracking-tight"
            data-testid="hero-title"
          >
            <span className="block text-white">{t.hero.title1}</span>
            <span className="block text-baj-grad">{t.hero.title2}</span>
            <span className="block text-white">{t.hero.title3}</span>
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#follow-me"
              onClick={scrollTo("#follow-me")}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta1}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#follow-me"
              onClick={scrollTo("#follow-me")}
              className="btn-ghost"
              data-testid="hero-cta-secondary"
            >
              {t.hero.cta2}
            </a>
          </div>

          {/* Stat pills */}
          <div className="mt-12 grid grid-cols-3 gap-3 sm:gap-5 max-w-xl">
            {[
              { Icon: Spade, label: "Cartes" },
              { Icon: Dices, label: "Dés" },
              { Icon: Heart, label: "Rires" },
            ].map(({ Icon, label }, i) => (
              <div
                key={label}
                className="glass rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-fuchsia-300" strokeWidth={2.5} />
                </div>
                <span className="text-sm font-semibold text-slate-200">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-400 text-xs flex flex-col items-center gap-2 opacity-80">
        <span className="tracking-[0.3em] uppercase">scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
      </div>
    </section>
  );
};

const FloatingDeco = () => (
  <div className="pointer-events-none absolute inset-0 -z-0">
    {/* Card-shaped floating tiles */}
    <div
      className="absolute right-[6%] top-[14%] w-32 h-44 rounded-3xl bg-gradient-to-br from-orange-500 to-pink-500 shadow-2xl shadow-orange-500/30 rotate-[10deg] float-slow hidden md:flex items-center justify-center"
      style={{ "--r": "10deg" }}
    >
      <Spade className="w-10 h-10 text-white/90" strokeWidth={2.5} />
    </div>
    <div
      className="absolute right-[22%] top-[42%] w-28 h-40 rounded-3xl bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-2xl shadow-fuchsia-500/40 rotate-[-8deg] float-slower hidden md:flex items-center justify-center"
      style={{ "--r": "-8deg" }}
    >
      <Dices className="w-10 h-10 text-white/95" strokeWidth={2.5} />
    </div>
    <div
      className="absolute right-[3%] bottom-[14%] w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 shadow-2xl shadow-cyan-400/40 rotate-[14deg] float-slow hidden lg:flex items-center justify-center"
      style={{ "--r": "14deg" }}
    >
      <Zap className="w-8 h-8 text-white" strokeWidth={2.5} />
    </div>
    {/* Sparkle dots */}
    <div className="absolute left-[40%] top-[20%] w-2 h-2 rounded-full bg-fuchsia-300/80 shadow-[0_0_18px_4px_rgba(217,70,239,0.6)]" />
    <div className="absolute left-[55%] top-[60%] w-1.5 h-1.5 rounded-full bg-orange-300/90 shadow-[0_0_18px_4px_rgba(255,107,0,0.55)]" />
    <div className="absolute left-[20%] top-[78%] w-2 h-2 rounded-full bg-cyan-300/80 shadow-[0_0_18px_4px_rgba(34,211,238,0.55)]" />
  </div>
);
